
import { createClient } from 'contentful';
import { Content, ContentCategory } from '@/types/content';

// Contentful space credentials - using the existing ones from contentfulService
const CONTENTFUL_SPACE_ID = 'v27mnfvqezea';
const CONTENTFUL_ACCESS_TOKEN = 'yPT2AwkIx-x2PudjtX0PMCoN4xkJ-aRZNIxOaeC9kjo';

// Initialize the Contentful client
const contentfulClient = createClient({
  space: CONTENTFUL_SPACE_ID,
  accessToken: CONTENTFUL_ACCESS_TOKEN,
});

// Types to match Contentful video content model
interface ContentfulVideoFields {
  title: string;
  description?: string;
  excerpt?: string; 
  thumbnail?: { fields: { file: { url: string } } };
  thumb?: { fields: { file: { url: string } } };
  videoUrl?: string;
  muxPlaybackId?: string;
  youtubeVideoId?: string;
  vimeoId?: string;
  vimeo?: string;
  genre?: string | string[];
  category?: string;
  tags?: string[];
  releaseDate?: string;
  featured?: boolean;
  maturityRating?: string;
  duration?: string | number;
  slug?: string;
}

// Transform Contentful entries to our app's video content model
const transformVideo = (entry: any): Content => {
  const fields = entry.fields as ContentfulVideoFields;
  
  // Get thumbnail URL, prioritizing 'thumb' field, then 'thumbnail', then fallback
  let thumbnailUrl = 'https://images.unsplash.com/photo-1605810230434-7631ac76ec81';
  
  if (fields.thumb && fields.thumb.fields && fields.thumb.fields.file) {
    thumbnailUrl = `https:${fields.thumb.fields.file.url}`;
  } else if (fields.thumbnail && fields.thumbnail.fields && fields.thumbnail.fields.file) {
    thumbnailUrl = `https:${fields.thumbnail.fields.file.url}`;
  }
  
  // Convert genre to array if it's a string
  const genreValue = fields.category || fields.genre || 'Uncategorized';
  const genre = Array.isArray(genreValue) ? genreValue : [genreValue];
  
  // Check for Vimeo ID in either vimeoId or vimeo field
  const vimeoId = fields.vimeoId || fields.vimeo || '';
  
  // Process tags
  const tags = Array.isArray(fields.tags) ? fields.tags : [];
  
  return {
    id: entry.sys.id,
    title: fields.title || 'Untitled Video',
    description: fields.description || '',
    thumbnail: thumbnailUrl,
    thumbnailUrl: thumbnailUrl,
    videoUrl: fields.videoUrl || '',
    muxPlaybackId: fields.muxPlaybackId || '',
    videoId: fields.youtubeVideoId || vimeoId || '',
    vimeoId: vimeoId,
    genre: genre,
    tags: tags,
    releaseDate: fields.releaseDate || new Date().toISOString(),
    releaseYear: fields.releaseDate ? fields.releaseDate.substring(0, 4) : new Date().getFullYear().toString(),
    duration: typeof fields.duration === 'number' ? fields.duration.toString() : (fields.duration || '0'),
    featured: fields.featured || false,
    maturityRating: fields.maturityRating || 'NR',
    excerpt: fields.excerpt || '',
    slug: fields.slug || entry.sys.id,
  };
};

// Service functions
export const contentfulVideoService = {
  // Get all videos
  getVideos: async (): Promise<Content[]> => {
    try {
      console.log('Fetching videos from Contentful...');
      
      const response = await contentfulClient.getEntries({
        content_type: 'video',
        include: 1,
      });
      
      console.log(`Successfully fetched ${response.items.length} videos from Contentful`);
      
      if (response.items.length === 0) {
        console.log('No videos found in Contentful.');
        return [];
      }
      
      return response.items.map(transformVideo);
    } catch (error) {
      console.error('Error fetching videos from Contentful:', error);
      
      // More detailed error logging
      if (error instanceof Error) {
        console.error(`Error message: ${error.message}`);
      }
      
      return [];
    }
  },
  
  // Get a single video by ID
  getVideoById: async (id: string): Promise<Content | null> => {
    try {
      console.log(`Fetching video with ID: ${id}`);
      const response = await contentfulClient.getEntry(id);
      
      if (response) {
        console.log('Video found in Contentful');
        return transformVideo(response);
      }
      
      console.log('Video not found in Contentful');
      return null;
    } catch (error) {
      console.error(`Error fetching video with ID ${id}:`, error);
      return null;
    }
  },

  // New function: Get a video by slug
  getVideoBySlug: async (slug: string): Promise<Content | null> => {
    try {
      console.log(`Fetching video with slug: ${slug}`);
      
      // Query Contentful for entries with matching slug
      const response = await contentfulClient.getEntries({
        content_type: 'video',
        'fields.slug': slug,
        include: 1,
        limit: 1
      });
      
      if (response.items && response.items.length > 0) {
        console.log('Video found by slug in Contentful');
        return transformVideo(response.items[0]);
      }
      
      console.log('No video found with slug:', slug);
      
      // If no match by slug, try to get by ID as fallback
      // This handles cases where the slug might actually be an ID
      try {
        const idResponse = await contentfulClient.getEntry(slug);
        if (idResponse) {
          console.log('Video found by using slug as ID in Contentful');
          return transformVideo(idResponse);
        }
      } catch (innerError) {
        console.log('Slug is not a valid entry ID either');
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching video with slug ${slug}:`, error);
      return null;
    }
  },
  
  // Get videos by category/genre
  getVideosByCategory: async (category: string): Promise<Content[]> => {
    try {
      console.log(`Fetching videos for category: ${category}`);
      
      // Try to fetch by category field first
      const response = await contentfulClient.getEntries({
        content_type: 'video',
        'fields.category': category,
        include: 1,
      });
      
      // If no results, try by genre field
      if (response.items.length === 0) {
        const genreResponse = await contentfulClient.getEntries({
          content_type: 'video',
          'fields.genre': category,
          include: 1,
        });
        return genreResponse.items.map(transformVideo);
      }
      
      console.log(`Found ${response.items.length} videos in category '${category}'`);
      return response.items.map(transformVideo);
    } catch (error) {
      console.error(`Error fetching videos for category ${category}:`, error);
      return [];
    }
  },
  
  // Get featured videos
  getFeaturedVideos: async (): Promise<Content[]> => {
    try {
      console.log(`Fetching featured videos`);
      const response = await contentfulClient.getEntries({
        content_type: 'video',
        'fields.featured': true,
        include: 1,
      });
      
      console.log(`Found ${response.items.length} featured videos`);
      return response.items.map(transformVideo);
    } catch (error) {
      console.error('Error fetching featured videos:', error);
      return [];
    }
  },
  
  // Get all available categories from Contentful
  getAllCategories: async (): Promise<string[]> => {
    try {
      // Get all videos first
      const videos = await contentfulVideoService.getVideos();
      
      // Extract unique categories
      const categories = new Set<string>();
      videos.forEach(video => {
        if (Array.isArray(video.genre)) {
          video.genre.forEach(g => categories.add(g));
        } else if (video.genre) {
          categories.add(video.genre);
        }
      });
      
      return Array.from(categories);
    } catch (error) {
      console.error('Error getting categories:', error);
      return [];
    }
  },
  
  // Get all available tags from Contentful videos
  getAllTags: async (): Promise<string[]> => {
    try {
      console.log('Fetching all tags from videos...');
      
      // Get all videos first
      const videos = await contentfulVideoService.getVideos();
      
      // Extract unique tags
      const allTags: string[] = [];
      videos.forEach(video => {
        if (Array.isArray(video.tags)) {
          video.tags.forEach(tag => {
            if (tag && typeof tag === 'string' && !allTags.includes(tag)) {
              allTags.push(tag);
            }
          });
        }
      });
      
      console.log(`Found ${allTags.length} unique tags from videos`);
      return allTags.sort();
      
    } catch (error) {
      console.error('Error fetching video tags:', error);
      return [];
    }
  },
  
  // Group videos by category to create playlists
  getVideoCategories: async (): Promise<ContentCategory[]> => {
    try {
      const allVideos = await contentfulVideoService.getVideos();
      
      // Group videos by category/genre
      const videosByCategory: Record<string, Content[]> = {};
      
      allVideos.forEach(video => {
        const genres = Array.isArray(video.genre) ? video.genre : [video.genre];
        
        genres.forEach(genre => {
          if (!genre) return; // Skip empty genres
          
          const category = String(genre);
          if (!videosByCategory[category]) {
            videosByCategory[category] = [];
          }
          videosByCategory[category].push(video);
        });
      });
      
      // Transform grouped videos into ContentCategory array
      return Object.entries(videosByCategory).map(([category, videos], index) => ({
        id: `category-${index}`,
        name: category,
        contents: videos,
      }));
    } catch (error) {
      console.error('Error getting video categories:', error);
      return [];
    }
  }
};

export default contentfulVideoService;
